import { useSnackbar } from "notistack";
import updateDocDataFieldApi from "../api/database/updateDocDataFieldApi";
import {
  deleteDocByRef,
  deleteDocDataField,
  getDocDataById,
  getDocDataByRef,
  getFlutterTimeFromSeconds,
  getRefFromId,
} from "../models/DataFunctions";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useAppContext } from "../config/AppContext";
import PrimaryButton from "../buttons/PrimaryButton";
import WarningButton from "../buttons/WarningButton";
import SecondaryButton from "../buttons/SecondaryButton";
import TextField2T from "../textfields/TextField2T";
import MLTF1 from "../textfields/MLTF1";
import dayjs from "dayjs";
import MLTFGPT1 from "../textfields/MLTFGPT1";
import createDocApi from "../api/database/createDocApi";
import updateDocApi from "../api/database/updateDocApi";
import { getOrgById } from "../models/OrgFunctions";

const SMSReminderModal = ({
  eventData,
  orgData,
  folderData,
  userData,
  onClose,
  setEvent,
  handleRefresh,
  navigateWorkspace,
  settings = false,
}) => {
  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;
  const [num_particulier, setNum_particulier] = useState(
    eventData?.num_particulier || ""
  );
  const [msg_content, setMsg_content] = useState(eventData?.msg_content || "");
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  function addVariables(content) {
    const variables = {
      "@nom": folderData.nom,
      "@ville": folderData.ville,
      "@adresse": folderData.adresse,
      "@postal": folderData.postal,

      "@client": folderData.marque || client.name,
      "@date": eventData
        ? dayjs.unix(eventData.start.seconds).format("DD/MM/YYYY")
        : "",
      "@heure": eventData
        ? dayjs.unix(eventData.start.seconds).format("HH:mm")
        : "",
    };
    let newContent = content;
    for (const [key, value] of Object.entries(variables)) {
      newContent = newContent.replace(key, value);
    }
    return newContent;
  }

  useEffect(() => {
    if (!eventData && !settings) return;
    handleFetchData();
  }, [eventData]);

  const handleFetchData = async () => {
    if (!settings) {
      if (eventData?.reminder) {
        setNum_particulier(eventData?.reminder?.num_particulier);
        setMsg_content(eventData?.reminder?.msg_content);
      } else {
        if (eventData.folder) {
          let folderData = await getDocDataByRef(eventData.folder);

          if (folderData) {
            let master = await getOrgById(folderData.master_id);

            setNum_particulier(folderData.reglement?.infos?.phone);
            if (orgData.config?.reminders?.sms?.msg_content) {
              setMsg_content(
                addVariables(
                  orgData.config?.reminders?.sms?.msg_content || ""
                ).substring(0, 150) || ""
              );
            } else {
              setMsg_content(
                addVariables(
                  `Bonjour, ceci est un rappel pour l'intervention de ${master.name} le @date à @heure à @adresse @postal @ville`
                ).substring(0, 150)
              );
            }
          }
        }
      }
    } else {
      if (orgData.config?.reminders?.sms) {
        setMsg_content(
          (orgData.config?.reminders?.sms?.msg_content || "").substring(0, 150)
        );
      } else {
        setMsg_content(
          `Bonjour, ceci est un rappel pour l'intervention de ${orgData.name} le @date à @heure à @adresse @postal @ville`.substring(
            0,
            150
          )
        );
      }
    }

    setLoading(false);
  };

  const handleDeleteReminder = async () => {
    const eventRef = getRefFromId("event", eventData?.id);

    if (eventData.job) {
      await deleteDocByRef(eventData?.reminder?.job_ref);
      await deleteDocDataField(eventRef, "job");
    }

    await deleteDocDataField(eventRef, "reminder");

    handleClickVariant("Rappel sms supprimé", "success");
    onClose();
    let new_event = await getDocDataById("event", eventData?.id);
    setEvent(new_event);
    handleRefresh();
  };

  const handleSaveConfig = async () => {
    if (msg_content.length > 150) {
      handleClickVariant("Le sms ne doit pas dépasser 150 caractères", "error");
      return;
    }
    await updateDocDataFieldApi(
      orgData.ref,
      "config.reminders.sms.msg_content",
      msg_content,
      prefix
    );
    handleClickVariant("Configuration des rappels sms enregistrée", "success");
  };

  const handleUpdateReminder = async () => {
    if (msg_content.length > 150) {
      handleClickVariant("Le sms ne doit pas dépasser 150 caractères", "error");
      return;
    }
    const eventRef = getRefFromId("event", eventData?.id);
    await updateDocApi(
      eventRef,
      {
        reminder: {
          num_particulier: num_particulier,
          msg_content: msg_content,
          status: "wait",
        },
      },
      prefix
    );
    if (eventData?.reminder?.job_ref) {
      await updateDocApi(
        eventData?.reminder?.job_ref,
        {
          date: dayjs
            .unix(eventData.start.seconds - 3600 * 24)
            .format("DD/MM/YYYY"),
        },
        prefix
      );
    }

    handleClickVariant("Rappel sms mis à jour", "success");
    onClose();
    let new_event = await getDocDataById("event", eventData?.id);
    setEvent(new_event);
    handleRefresh();
  };

  const handleSetReminder = async () => {
    if (msg_content.length > 150) {
      handleClickVariant("Le sms ne doit pas dépasser 150 caractères", "error");
      return;
    }

    // create job object
    const eventRef = getRefFromId("event", eventData?.id);

    const daybefore = new Date(eventData?.start.seconds * 1000) - 86400000;
    const daybeforeformatted = moment(daybefore).format("DD/MM/YYYY");
    const body = {
      to: num_particulier,
      html_content: msg_content,
      type: "sms",
      date: daybeforeformatted,
      trigger_date: getFlutterTimeFromSeconds(moment(daybefore).unix()),
      sender_id: orgData.id,
      emails: [],
      status: "pending",
      nom: eventData?.name,
      event_ref: eventRef,
    };
    const new_job = await createDocApi("job", body, prefix);
    // create job object
    const jobRef = getRefFromId("job", new_job.id);

    await updateDocApi(
      eventRef,
      {
        reminder: {
          num_particulier: num_particulier,
          msg_content: msg_content,
          status: "wait",
          job_ref: jobRef,
        },
      },
      prefix
    );

    handleClickVariant("Rappel sms défini", "success");
    onClose();
    let new_event = await getDocDataById("event", eventData?.id);
    setEvent(new_event);
    handleRefresh();
  };

  return (
    <div
      className={`w-c400 flex flex-col overflow-auto h-fit   bg-white rounded-xl ${settings ? "" : ""}`}
    >
      <div className="flex flex-col  h-fit ">
        {!settings && (
          <>
            <div className="text-h2 flex flex-row items-center justify-between p-4 border-b-1">
              Rappel sms visite chantier
              <SecondaryButton onClick={() => onClose()}>
                Fermer
              </SecondaryButton>
            </div>
            <div className="flex flex-col gap-2 bg-neutral-50 p-4">
              <div className="flex flex-row gap-2">
                <div className="text-h3">Status</div>
                <div
                  className={`text-h3 rounded-lg px-2 py-0.5 ${eventData?.reminder?.status === "sent" ? "bg-green-100" : eventData?.reminder?.status === "wait" ? "bg-orange-100" : "bg-neutral-200"}`}
                >
                  {eventData?.reminder?.status === "sent"
                    ? "Envoyé"
                    : eventData?.reminder?.status === "wait"
                      ? "Programmé"
                      : "Non défini"}
                </div>
              </div>
              <div className="flex flex-row gap-2">
                <div className="text-h3">Date d'envoi</div>
                <div className="text-h3">
                  {dayjs
                    .unix(eventData?.start.seconds - 3600 * 24)
                    .format("DD/MM/YYYY HH:mm")}
                </div>
              </div>
              <TextField2T
                label={"Numéro de téléphone"}
                value={num_particulier}
                disabled={eventData?.reminder?.status === "sent"}
                type="phone"
                onChange={(e) => setNum_particulier(e.target.value)}
              ></TextField2T>{" "}
              <MLTFGPT1
                label={"Message"}
                transparent={false}
                disabled={eventData?.reminder?.status === "sent"}
                value={msg_content}
                onChange={(e) =>
                  setMsg_content(e.target.value.substring(0, 150))
                }
              ></MLTFGPT1>
              <div className="text-h3 text-gray-600 flex flex-col gap-1 ">
                Max caractères restants : {150 - msg_content?.length}
              </div>
            </div>
            <div className="flex flex-row justify-end gap-2 p-4 border-t-1">
              <SecondaryButton
                onClick={() => navigateWorkspace("/reminders?tab=settings")}
              >
                Paramètres
              </SecondaryButton>

              {eventData?.reminder && (
                <WarningButton
                  disabled={eventData.reminder?.status === "sent"}
                  onClick={handleDeleteReminder}
                >
                  Supprimer
                </WarningButton>
              )}

              <PrimaryButton
                onClick={() => {
                  eventData?.reminder
                    ? handleUpdateReminder()
                    : handleSetReminder();
                }}
                disabled={
                  eventData.reminder?.status === "sent" ||
                  !num_particulier ||
                  num_particulier.length !== 14 ||
                  !msg_content || // date is in the past
                  dayjs.unix(eventData.start.seconds).isBefore(dayjs())
                }
              >
                Confirmer
              </PrimaryButton>
            </div>
          </>
        )}

        {settings && (
          <div className="flex flex-col gap-2 pb-40">
            <div className="text-h3 ">
              Modèle pour le sms de rappel de l'intervention
            </div>
            <MLTFGPT1
              label={"Message"}
              disabled={eventData?.reminder?.status === "sent"}
              value={msg_content}
              onChange={(e) => setMsg_content(e.target.value.substring(0, 150))}
            ></MLTFGPT1>
            <div className="text-h3 text-gray-600 flex flex-col gap-1 ">
              Max caractères restants : {150 - msg_content?.length}
            </div>

            <div className="flex flex-row justify-end gap-2">
              <PrimaryButton onClick={handleSaveConfig} disabled={!msg_content}>
                Enregistrer
              </PrimaryButton>
            </div>

            <div className="text-h3 text-gray-600 flex flex-col gap-1 mt-4 bg-slate-50 border-1 border-indigo-300 p-4 rounded-lg">
              <p className=" mb-3 ">Variables disponibles :</p>
              <p className="  ">
                <span className="text-indigo-500 font-semibold">@client</span>{" "}
                pour ajouter le nom du client (mandataire)
              </p>
              <p className="  ">
                <span className="text-indigo-500 font-semibold">@nom</span> pour
                ajouter le nom du dossier
              </p>
              <p className="  ">
                <span className="text-indigo-500 font-semibold">@adresse</span>{" "}
                pour ajouter l'adresse du dossier
              </p>{" "}
              <p className="  ">
                <span className="text-indigo-500 font-semibold">@postal</span>{" "}
                pour ajouter le code postal du dossier
              </p>{" "}
              <p className="  ">
                <span className="text-indigo-500 font-semibold">@ville</span>{" "}
                pour ajouter la ville du dossier
              </p>{" "}
              <p className="  ">
                <span className="text-indigo-500 font-semibold">@date</span>{" "}
                pour ajouter la date de l'évènement
              </p>
              <p className="  ">
                <span className="text-indigo-500 font-semibold">@heure</span>{" "}
                pour ajouter l'heure de l'évènement
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SMSReminderModal;
